import { useTranslation } from "react-i18next";
import {
  BaseAssignmentResponseDtoQuestionsItem,
  BaseFillTheBlankQuestionDto,
  BaseOpenEndedQuestionDto,
  BaseTestQuestionDto,
  BaseTrueFalseQuestionDto,
} from "@packages/api/madlenAPI.schemas";
import { TestQuestionEditor } from "./TestQuestionEditor";
import { FillBlankQuestionEditor } from "./FillBlankQuestionEditor";
import { TrueFalseQuestionEditor } from "./TrueFalseQuestionEditor";
import { useEffect, useState } from "react";
import { Input } from "@core/input";
import { X } from "lucide-react";
import { FileUploader } from "@core/file-uploader";
interface QuestionEditorProps {
  question: BaseAssignmentResponseDtoQuestionsItem;
  onQuestionChange: (question: BaseAssignmentResponseDtoQuestionsItem) => void;
  gradeLevel: number | undefined;
}

export const QuestionEditor = ({
  question,
  onQuestionChange,
  gradeLevel,
}: QuestionEditorProps) => {
  const { t } = useTranslation();
  const [uploadError, setUploadError] = useState<string | null>(null);

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useEffect(() => {
    // Adjust the height of the textarea when the question changes
    const textareas = document.querySelectorAll("textarea");
    textareas.forEach((textarea) => {
      adjustTextareaHeight(textarea as HTMLTextAreaElement);
    });
  }, [question]);

  return (
    <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {t("assignments.generator.preview.questionImage")}
        </label>
        <div className="flex flex-col space-y-2">
          {!question.mediaUrl && (
            <FileUploader
              accept={{ "image/*": [".png", ".jpg", ".jpeg"] }}
              maxSize={15 * 1024 * 1024}
              onUploadSuccess={(_, __, downloadUrl) => {
                onQuestionChange({
                  ...question,
                  mediaUrl: downloadUrl,
                });
              }}
              onFileDelete={() => {
                onQuestionChange({
                  ...question,
                  mediaUrl: undefined,
                });
              }}
              onUploadError={setUploadError}
              dragActiveText={t("assignments.generator.preview.dropHere")}
              dragInactiveText={t("assignments.generator.preview.drop")}
              shouldExtractContent={false}
              uploadColor="blue"
              extractColor="blue"
              successColor="green"
            />
          )}

          {uploadError && (
            <div className="text-sm text-red-600 dark:text-red-400">
              {uploadError}
            </div>
          )}

          {question.mediaUrl && (
            <div className="mt-2 w-full rounded-md overflow-hidden relative group bg-gradient-to-r from-blue-50/30 to-indigo-50/30 dark:from-blue-900/20 dark:to-indigo-900/20 border border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-center p-4">
                <img
                  src={question.mediaUrl}
                  alt={t("assignments.generator.preview.questionImage")}
                  className="max-h-60 max-w-full rounded shadow-sm"
                />
              </div>
              <div className="absolute top-2 right-2">
                <button
                  type="button"
                  onClick={() =>
                    onQuestionChange({ ...question, mediaUrl: undefined })
                  }
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-md transition-all opacity-0 group-hover:opacity-100"
                  title={t("assignments.generator.preview.removeImage")}
                  aria-label={t("assignments.generator.preview.removeImage")}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {question.type !== "FB" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            {t("assignments.generator.preview.questionText")}
          </label>
          <textarea
            value={question.body}
            onChange={(e) => {
              adjustTextareaHeight(e.target);
              onQuestionChange({
                ...question,
                body: e.target.value,
              });
            }}
            className="w-full p-2 border rounded-md dark:bg-gray-600 dark:border-gray-500 dark:text-gray-100 overflow-hidden"
            style={{ minHeight: "4.5rem" }}
          />
        </div>
      )}

      {question.type === "TEST" && (
        <TestQuestionEditor
          question={question as BaseTestQuestionDto}
          onQuestionChange={onQuestionChange}
          gradeLevel={gradeLevel}
        />
      )}

      {question.type === "FB" && (
        <FillBlankQuestionEditor
          question={question as BaseFillTheBlankQuestionDto}
          onQuestionChange={onQuestionChange}
        />
      )}

      {question.type === "TF" && (
        <TrueFalseQuestionEditor
          question={question as BaseTrueFalseQuestionDto}
          onQuestionChange={onQuestionChange}
        />
      )}

      {question.type === "OE" && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            {t("assignments.generator.preview.correctAnswer")}
          </label>
          <textarea
            value={(question as BaseOpenEndedQuestionDto).answer}
            onChange={(e) => {
              adjustTextareaHeight(e.target);
              onQuestionChange({
                ...question,
                answer: e.target.value,
              });
            }}
            className="w-full p-2 border rounded-md dark:bg-gray-600 dark:border-gray-500 dark:text-gray-100 overflow-hidden"
            style={{ minHeight: "4.5rem" }}
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {t("assignments.generator.preview.explanation")}
        </label>
        <textarea
          value={question.explanation || ""}
          onChange={(e) => {
            adjustTextareaHeight(e.target);
            onQuestionChange({
              ...question,
              explanation: e.target.value,
            });
          }}
          className="w-full p-2 border rounded-md dark:bg-gray-600 dark:border-gray-500 dark:text-gray-100 overflow-hidden"
          style={{ minHeight: "4.5rem" }}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {t("assignments.generator.preview.point")}
        </label>
        <Input
          type="number"
          value={question.point}
          onChange={(e) =>
            onQuestionChange({
              ...question,
              point: Number(e.target.value),
            })
          }
          min={0}
          max={100}
          classNames={{
            input:
              "w-full p-2 border rounded-md dark:bg-gray-600 dark:border-gray-500 dark:text-gray-100",
          }}
        />
      </div>
    </div>
  );
};
